import { logger } from './logger';

const BASE_URL = 'https://api.dropboxapi.com/2';
const CONTENT_BASE_URL = 'https://content.dropboxapi.com/2';
const APP_KEY = import.meta.env.VITE_DROPBOX_APP_KEY;

let accessToken = null;

const dropboxClient = {
  setAccessToken(token) {
    accessToken = token;
  },

  getAccessToken() {
    return accessToken;
  },

  async request(method, path, body = null, useContentApi = false) {
    if (!accessToken) {
      throw new Error('Access token not set. Please authenticate first.');
    }

    const baseUrl = useContentApi ? CONTENT_BASE_URL : BASE_URL;
    const url = `${baseUrl}${path}`;

    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    };

    // For content API and file uploads, adjust headers
    if (useContentApi && method === 'POST') {
      headers['Content-Type'] = 'application/octet-stream';
      if (body && typeof body === 'object') {
        headers['Dropbox-API-Arg'] = JSON.stringify(body);
        body = null; // File data would go in body for actual uploads
      }
    }

    const requestData = {
      headers: { ...headers, Authorization: 'Bearer [REDACTED]' },
      body: body ? JSON.stringify(body) : null
    };

    const logEntry = logger.logRequest('dropbox', method, url, requestData);
    const startTime = performance.now();

    try {
      const fetchOptions = {
        method,
        headers
      };

      if ((method === 'POST' || method === 'PUT') && body) {
        fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      const duration = Math.round(performance.now() - startTime);
      
      const contentType = response.headers.get('content-type') || '';
      let responseData;
      
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      logger.logResponse(logEntry.id, response.status, responseData, duration, {
        'content-type': contentType,
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return responseData;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      logger.logError(logEntry.id, error, 0);
      throw error;
    }
  },

  async refreshAccessToken(refreshToken) {
    const url = 'https://api.dropbox.com/oauth2/token';
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: APP_KEY
    });

    const logEntry = logger.logRequest('dropbox', 'POST', url, { body: body.toString() });
    const startTime = performance.now();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body
      });

      const duration = Math.round(performance.now() - startTime);
      const data = await response.json();

      logger.logResponse(logEntry.id, response.status, data, duration, {
        'content-type': 'application/json',
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      if (data.access_token) {
        this.setAccessToken(data.access_token);
        return data.access_token;
      } else {
        throw new Error('No access token in response');
      }
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      logger.logError(logEntry.id, error, 0);
      throw error;
    }
  },

  // File operations
  listFiles(path = '', recursive = false) {
    return this.request('POST', '/files/list_folder', { path, recursive });
  },

  uploadFile(path, fileData, mode = 'add', autorename = true) {
    const metadata = { path, mode, autorename };
    return this.request('POST', '/files/upload', metadata, true);
  },

  downloadFile(path) {
    return this.request('POST', '/files/download', { path }, true);
  }
};

export { dropboxClient };